import React, { useEffect, useState } from 'react';
import './ResetPassword.css';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../services/AxiosInstance';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const inviteCorporateId = searchParams.get('corporateId');
  const isInvite = searchParams.get('invite') === '1' || (token && token.startsWith('inv15.'));

  const [corporateList, setCorporateList] = useState([]);
  const [selectedCorporateId, setSelectedCorporateId] = useState('');
  const [corporateDisabled, setCorporateDisabled] = useState(false);
  const [corporateName, setCorporateName] = useState('');
  const [linkError, setLinkError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const initialValues = { password: '', confirmPassword: '' };

  const validationSchema = Yup.object({
    password: Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(/[0-9]/, "Password must contain at least one number")
      .matches(/[!@#$%^&*(),.?":{}|<>]/, "Password must contain at least one special character"),

    confirmPassword: Yup.string()
      .required("Repeat password is required")
      .oneOf([Yup.ref("password"), null], "Passwords must match"),
  });

  useEffect(() => {
    const getCorporatelist = async () => {
      try {
        const response = await axiosInstance.post('/auth/forgot-password/corporatelist', {
          email,
          token,
          ...(inviteCorporateId ? { corporateId: inviteCorporateId } : {}),
        });

        const list = response.data.data || [];
        setCorporateList(list);

        if (list.length === 1) {
          setSelectedCorporateId(list[0].corporate_id);
          setCorporateDisabled(true);
          setCorporateName(list[0].Corporate?.name || '');
        } else if (inviteCorporateId) {
          const match = list.find((c) => String(c.corporate_id) === String(inviteCorporateId));
          if (match) {
            setSelectedCorporateId(match.corporate_id);
            setCorporateDisabled(true);
            setCorporateName(match.Corporate?.name || '');
          }
        }
      } catch (error) {
        const message =
          error.response?.data?.message ||
          (isInvite
            ? 'Invalid or expired invitation link.'
            : 'Invalid or expired reset link.');
        setLinkError(message);
        toast.error(message);
      }
    };

    if (email && token) {
      getCorporatelist();
    } else {
      setLinkError('Invalid link. Email and token are required.');
    }
  }, [email, token, inviteCorporateId, isInvite]);

  const onSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const corporateid =
        selectedCorporateId ||
        (corporateList.length === 1 ? corporateList[0].corporate_id : null);

      if (!corporateid) {
        toast.error('Please select a corporate.');
        setSubmitting(false);
        return;
      }

      await axiosInstance.post('/auth/forgotpassword/reset', {
        token,
        newPassword: values.password,
        corporateid,
      });

      toast.success(
        isInvite
          ? 'Password set successfully! You can now log in.'
          : 'Password reset successful! You can now log in.'
      );
      resetForm();
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Failed to set password. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const pageTitle = isInvite ? 'Set Your Password' : 'Reset Password';
  const submitLabel = isInvite ? 'Set Password' : 'Reset Password';
  const submittingLabel = isInvite ? 'Saving...' : 'Resetting...';

  return (
    <div className="d-flex flex-column justify-content-start align-items-center min-vh-100" style={{ backgroundColor: '#f8f8f8' }}>
      <ToastContainer />
      <div className='hero-landing'>
        <h1 className="text-center mb-4 text-white display-5">{pageTitle}</h1>
      </div>
      <div style={{ maxWidth: 400, width: '100%', padding: "2rem", marginBottom: "5rem" }}>
        {linkError ? (
          <div className="alert alert-danger" role="alert">
            {linkError}
          </div>
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({ isSubmitting }) => (
              <Form autoComplete="off">
                {isInvite && corporateName && (
                  <div className="mb-3 text-center">
                    <p className="mb-1 text-muted" style={{ fontSize: '0.9rem' }}>Joining organization</p>
                    <h4 className="mb-0" style={{ color: '#C42126' }}>{corporateName}</h4>
                  </div>
                )}

                {/* Corporate List Dropdown (forgot-password multi-corp only) */}
                {!isInvite && corporateList.length > 1 && (
                  <div className="mb-3">
                    <label htmlFor="corporateId" className="form-label"><strong>Select Corporate</strong></label>
                    <Field
                      as="select"
                      name="corporateId"
                      className="form-control"
                      value={selectedCorporateId}
                      onChange={(e) => setSelectedCorporateId(e.target.value)}
                      disabled={corporateDisabled}
                      autoComplete="off"
                    >
                      <option value="">-- Select Corporate --</option>
                      {corporateList.map((corp) => (
                        <option key={corp.corporate_id} value={corp.corporate_id}>
                          {corp.Corporate.name}
                        </option>
                      ))}
                    </Field>
                  </div>
                )}

                {!isInvite && corporateList.length === 1 && corporateName && (
                  <div className="mb-3">
                    <label className="form-label"><strong>Corporate</strong></label>
                    <input type="text" className="form-control" value={corporateName} disabled readOnly autoComplete="off" />
                  </div>
                )}

                <div className="mb-3">
                  <label htmlFor="password" className="form-label"><strong>New Password</strong></label>
                  <div className="position-relative password-input-wrapper">
                    <Field
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className="form-control"
                      placeholder="Enter new password"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                  <ErrorMessage name="password" component="div" className="text-danger small mt-1" />
                </div>

                <div className="mb-3">
                  <label htmlFor="confirmPassword" className="form-label"><strong>Repeat Password</strong></label>
                  <div className="position-relative password-input-wrapper">
                    <Field
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      className="form-control"
                      placeholder="Repeat new password"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                  <ErrorMessage name="confirmPassword" component="div" className="text-danger small mt-1" />
                </div>
                <div className="password-info">Password must be at least 8 characters long and contain at least one uppercase letter, one number, and one special symbol.</div>
                <button
                  type="submit"
                  className="reset-password-btn"
                  disabled={isSubmitting || !selectedCorporateId}
                >
                  {isSubmitting ? submittingLabel : submitLabel}
                </button>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
